import ChatRounded from '@mui/icons-material/ChatRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import SettingsRounded from '@mui/icons-material/SettingsRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { businessDate } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { useWhatsappAdmin, useWhatsappStatus } from './api';
import { WHATSAPP_GREEN } from './WhatsappSend';

const ASK = '/support?message=' + encodeURIComponent('Hi, I want the WhatsApp feature');
const FEATURE =
  'Send invoices, receipts, orders and statements to customers and suppliers on WhatsApp, from your shop’s own number.';

/**
 * The WhatsApp add-on on the Subscription page. Not enabled: dimmed with a lock and a quick way to
 * ask for it. Enabled: its period and grace, the shop's on/off switch (off hides WhatsApp but
 * keeps the number linked) and the linked number.
 */
export function WhatsappCard() {
  const { can } = useAuth();
  const { format } = useShop();
  const notify = useNotify();
  const status = useWhatsappStatus();
  const admin = useWhatsappAdmin();
  const plugin = status.data?.plugin;
  if (!plugin) return null;

  if (plugin.state === 'NOT_ENABLED')
    return (
      <Tooltip
        title="Contact us to add WhatsApp for a small monthly fee."
        placement="top"
        describeChild
      >
        <Card sx={{ position: 'relative' }}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
              <Box sx={{ position: 'relative', opacity: 0.45 }}>
                <WhatsAppIcon sx={{ fontSize: 40, color: WHATSAPP_GREEN }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="h6">WhatsApp</Typography>
                  <Chip size="small" icon={<LockRounded />} label="Add-on" variant="outlined" />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {FEATURE}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Contact us to add it for a small monthly fee.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ChatRounded />}
                  component={RouterLink}
                  to={ASK}
                  sx={{ mt: 1.5 }}
                >
                  Ask for WhatsApp
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Tooltip>
    );

  const chip =
    plugin.state === 'ACTIVE'
      ? { label: 'Active', color: 'success' as const }
      : plugin.state === 'GRACE'
        ? { label: 'In grace', color: 'warning' as const }
        : { label: 'Ended', color: 'error' as const };
  const connection = status.data?.connection;
  const day = (iso: string | null) => (iso ? businessDate(iso.slice(0, 10), format) : '—');

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
          <WhatsAppIcon sx={{ fontSize: 40, color: WHATSAPP_GREEN }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="h6">WhatsApp</Typography>
              <Chip size="small" color={chip.color} label={chip.label} variant="outlined" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {plugin.state === 'EXPIRED'
                ? 'WhatsApp subscription ended, grace used. Contact PackBytes to renew it.'
                : `Paid through ${day(plugin.paidThrough)} · works until ${day(plugin.graceEndsAt)} with grace`}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {connection?.status === 'LINKED'
                ? `Sending from +${connection.phone ?? ''}`
                : 'No WhatsApp number linked yet.'}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mt: 1.5, alignItems: { sm: 'center' } }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={plugin.shopEnabled}
                    disabled={!can('whatsapp.manage') || admin.settings.isPending}
                    onChange={(e) =>
                      admin.settings.mutate(
                        { shopEnabled: e.target.checked, version: plugin.version ?? 1 },
                        { onError: (err) => notify.error(errorMessage(err)) },
                      )
                    }
                  />
                }
                label={plugin.shopEnabled ? 'Shown in the shop' : 'Hidden in the shop'}
              />
              {can('whatsapp.manage') && (
                <Button
                  size="small"
                  startIcon={<SettingsRounded />}
                  component={RouterLink}
                  to="/settings?tab=whatsapp"
                >
                  {connection?.status === 'LINKED' ? 'WhatsApp settings' : 'Link WhatsApp'}
                </Button>
              )}
              {plugin.state !== 'ACTIVE' && (
                <Button size="small" startIcon={<ChatRounded />} component={RouterLink} to={ASK}>
                  Contact us
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
